"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { readSharedTheme, writeSharedTheme } from "./shared-theme-cookie";

export type ScopedTheme = "light" | "dark";

type ScopedThemeValue = {
  readonly theme: ScopedTheme;
  readonly toggle: () => void;
};

/** Shared cookie first (website and docs host agree), then this host's own storage. */
function readStoredTheme(storageKey: string): ScopedTheme {
  const shared = readSharedTheme();
  if (shared !== null) return shared;
  try {
    return window.localStorage.getItem(storageKey) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

/** Idempotent — only touches DOM when a value actually differs. */
function applyThemeHtml(theme: ScopedTheme): void {
  const html = document.documentElement;
  const other: ScopedTheme = theme === "dark" ? "light" : "dark";
  if (html.classList.contains(other)) html.classList.remove(other);
  if (!html.classList.contains(theme)) html.classList.add(theme);
  if (html.style.colorScheme !== theme) html.style.colorScheme = theme;
  if (html.getAttribute("data-scoped-theme") !== theme) html.setAttribute("data-scoped-theme", theme);
}

/**
 * Holds `<html>` on the scoped theme against the app's next-themes (a nested ThemeProvider is a no-op).
 * Observer disconnects while applying, or class mutations loop synchronously.
 */
function ThemeLock({ theme }: { readonly theme: ScopedTheme }): null {
  useEffect(() => {
    applyThemeHtml(theme);
    const observer = new MutationObserver((): void => {
      observer.disconnect();
      applyThemeHtml(theme);
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [theme]);
  return null;
}

const ScopedThemeContext = createContext<ScopedThemeValue | null>(null);

/** Null outside a ScopedThemeShell. */
export function useScopedTheme(): ScopedThemeValue | null {
  return useContext(ScopedThemeContext);
}

/**
 * Own light/dark for one area (help center, website), remembered per browser under `storageKey`.
 * Never changes the app's theme. Pair with `scopedThemeBootstrap` for the first paint.
 */
export function ScopedThemeShell({
  storageKey,
  forcedTheme,
  children,
}: {
  readonly storageKey: string;
  /** Pins the theme on pages without a toggle; the stored choice survives for other pages. */
  readonly forcedTheme?: ScopedTheme;
  readonly children: ReactNode;
}): ReactNode {
  // Lazy: the bootstrap script already painted the stored theme; no light flash.
  const [theme, setTheme] = useState<ScopedTheme>(() =>
    typeof window === "undefined" ? "light" : readStoredTheme(storageKey),
  );
  const toggle = useCallback((): void => {
    setTheme((prev) => {
      const next: ScopedTheme = prev === "dark" ? "light" : "dark";
      writeSharedTheme(next);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // Private mode: the choice lasts until reload.
      }
      return next;
    });
  }, [storageKey]);
  // A dark choice made before the cookie existed reaches the other host too.
  useEffect(() => {
    if (theme === "dark" && readSharedTheme() === null) writeSharedTheme("dark");
  }, [theme]);
  const shownTheme = forcedTheme ?? theme;
  const value = useMemo(
    () => ({ theme: shownTheme, toggle }),
    [shownTheme, toggle],
  );
  return (
    <ScopedThemeContext.Provider value={value}>
      <ThemeLock theme={shownTheme} />
      {children}
    </ScopedThemeContext.Provider>
  );
}
